import type { ComponentType } from 'react';
import { Skeleton } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useActivity } from '@/hooks/useActivity';
import { formatDayStamp } from '@/utils/date';
import { getAppLogoUrl } from '@/constants/appIcons';
import { PersonAvatar } from '@/components/common/PersonAvatar';
import type {
  Activity, ActivityActor, ActivityEntityType, ActivityEntry,
} from '@/types/activity';

/**
 * The feed's own neutrals.
 *
 * Named here rather than taken from `taskTheme`: this renders for any entity,
 * and reaching into the task board for a colour is the dependency the module
 * boundary exists to avoid. They are the product's neutral hairline and two
 * greys, and they match what the surrounding panels use.
 */
const RAIL = 'bg-[rgba(0,0,0,0.08)]';

const CARD_BORDER = 'border-[rgba(0,0,0,0.08)]';

const INK = 'text-[#181818]';

const INK_2 = 'text-[#696969]';

const INK_3 = 'text-[#9A9A9A]';

const BODY = 'text-[#444444]';

/** Who made the change, as a face or as the product's own mark. */
const ActorMark = ({ actor }: { actor: ActivityActor }) => {
  if (actor.type !== 'user') {
    return (
      <img
        src={getAppLogoUrl('strivelabs')}
        alt=""
        className="size-[18px] shrink-0 rounded-full"
      />
    );
  }

  if (!actor.user) {
    return null;
  }

  return <PersonAvatar person={actor.user} className="size-[18px] text-[8px]" />;
};

const FeedSkeleton = () => (
  <div className="flex flex-col gap-4 py-2" role="status" aria-busy="true" aria-label="Loading activity">
    {[ 0, 1, 2 ].map((row) => (
      <div key={row} className="flex items-center gap-3">
        <Skeleton className="size-[31px] rounded-[9px]" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    ))}
  </div>
);

const Note = ({ children }: { children: string }) => (
  <p className={cn('py-6 text-center text-[13px]', INK_3)}>{children}</p>
);

interface ActivityFeedProps {
  entityType: ActivityEntityType;
  entityId: string;
  /**
   * What the person who made the change is called. The agent is named by the
   * feed itself, since that is the same on every timeline.
   */
  actorName: (actor: ActivityActor) => string;
  /** What one event says, or null to leave it off the timeline. */
  entryFor: (event: Activity) => ActivityEntry | null;
  /** The mark beside a line, by what the line is about. */
  iconFor: (entry: ActivityEntry) => ComponentType<{ className?: string }>;
  /** How the entity reads in a sentence, for the empty and failed states. */
  entityLabel: string;
}

/**
 * What has happened to something, newest first.
 *
 * The chrome is the same whatever the entity: fetching, the rail, the faces,
 * the timestamps, the empty and failed states. What differs per module is only
 * what each event says and which mark it wears, and those are handed in — so a
 * second module gets a timeline without a second component.
 *
 * A read-only record. Every line is written by the change it describes, so
 * there is nothing here to edit and no empty state to act on. An event the
 * caller's copy does not recognise is skipped rather than rendered blank, which
 * is what lets the server's catalogue grow ahead of a deployed client.
 */
export const ActivityFeed = ({
  entityType, entityId, actorName, entryFor, iconFor, entityLabel,
}: ActivityFeedProps) => {
  const { data, isLoading, isError } = useActivity(entityType, entityId);

  if (isLoading && !data) {
    return <FeedSkeleton />;
  }

  // Guarded the same way the skeleton is. Every save invalidates this key, so
  // an open timeline refetches on each edit; a blip on one of those must not
  // replace a page the reader is part-way through with an error.
  if (isError && !data) {
    return <Note>{`Could not load this ${entityLabel}'s activity.`}</Note>;
  }

  const lines = (data?.activity ?? [])
    .map((event) => ({ event, entry: entryFor(event) }))
    .filter((line): line is { event: Activity; entry: ActivityEntry } => line.entry !== null);

  if (lines.length === 0) {
    return <Note>{`Nothing has happened to this ${entityLabel} yet.`}</Note>;
  }

  // Measured against what the page returned rather than what rendered: lines
  // the copy did not recognise are not older history, and saying so would point
  // the reader at a control that does not exist.
  const returned = data?.activity.length ?? 0;
  const hidden = (data?.total ?? 0) - returned;

  return (
    <div className="relative py-1">
      {/* The rail behind the marks. Each mark carries the panel's own white so
          the line reads as passing behind it rather than through it. */}
      <span
        className={cn('pointer-events-none absolute bottom-4 left-[15px] top-4 w-px', RAIL)}
        aria-hidden
      />

      <ol className="relative flex flex-col">
        {lines.map(({ event, entry }) => {
          const Icon = iconFor(entry);

          return (
            <li key={event.id} className="grid grid-cols-[31px_1fr] gap-3 py-[7px]">
              <span
                className={cn(
                  'flex size-[31px] items-center justify-center rounded-[9px] bg-white border',
                  CARD_BORDER,
                )}
              >
                <Icon className={cn('size-3.5', INK_2)} />
              </span>

              <div className="min-w-0 pt-[3px]">
                <div className={cn(
                  'flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13.5px]', BODY,
                )}>
                  <ActorMark actor={event.actor} />
                  <span className={cn('font-semibold', INK)}>
                    {actorName(event.actor)}
                  </span>
                  <span>{entry.sentence}</span>
                </div>

                {/* One save that changed several fields is one thing somebody
                    did, so the fields sit under it rather than repeating the
                    name and the timestamp for each. */}
                {entry.details.length > 0 && (
                  <div className={cn('mt-1.5 flex flex-col gap-1 text-[12.5px]', INK_2)}>
                    {entry.details.map((detail) => (
                      <p key={detail.field} className="flex flex-wrap gap-x-1.5">
                        <span className={cn('font-medium', INK)}>{detail.label}</span>
                        <span>{detail.sentence}</span>
                      </p>
                    ))}
                  </div>
                )}

                <p className={cn('mt-[3px] text-[12px]', INK_3)}>
                  {formatDayStamp(event.created_at)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {hidden > 0 && (
        <p className={cn('pl-[43px] pt-2 text-[12px]', INK_3)}>
          Showing the most recent {returned} of {data?.total}.
        </p>
      )}
    </div>
  );
};
